import React, { useEffect, useState, Suspense } from "react";
import "./App.css";
import "./Theme/app.scss";
import "./Theme/main.scss"
import "./Theme/dark-mode.scss"
import CssBaseline from '@mui/material/CssBaseline';
import { ContextModel } from "./shared/Modal/ContextModel";
import { PreferencesContext } from "./PreferencesContext";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import { getStorage, setStorage } from "./Services/common/Storage";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { ColorModeProvider } from "./Theme/ColorModeContext";
import Alerts from "./Components/Alerts";
import { Backdrop, Box, CircularProgress } from "@mui/material";
import UnauthorizeDialog from "./Components/UnauthorizeDialog";
import SplashComponent from "./Navigation/splash";
import ScrollTop from "../src/Components/ScrollTop";
import offlineimg from './Assets/offline.png';
import { Info } from "./Services/AuthService";
import Nav from "./Navigation/navbar";
import { useNetworkCheck } from "../src/context/NetworkContext";
import { GetLabelList } from "./Services/LabelService";
import MaintenanceBanner, {
  MAINTENANCE_BANNER_HEIGHT,
  useMaintenanceBannerVisible,
} from "./Components/MaintenanceBanner";

function App() {
  const { isOnline } = useNetworkCheck();
  const [userContext, setUserContext] = React.useState<ContextModel>(getStorage());
  const [isLoading, setLoading] = useState(true);
  const showMaintenanceBanner = useMaintenanceBannerVisible();
  const isLoggedIn = Boolean(userContext?.token && userContext.token.length > 0);
  let navigate = useNavigate();
  let location = useLocation();
  const isDashboardRoute =
    location.pathname === "/dashboard" ||
    location.pathname === "/dashboard/";
  // Dashboard uses its own section skeletons — skip the global Splash line there.
  const showAppSplash = isLoading && !isDashboardRoute;

  const updateUserContext = React.useCallback((context) => {
    setStorage(context);
    return (setUserContext(context));
  }, [userContext]);

  const preferences = React.useMemo(
    () => ({
      updateUserContext, userContext,
    }), [userContext]
  );

  let publicPages = ['/', 'reset-password', 'set-password', 'forgot-password', 'manage-user', "terms", "privacy-policy", "android"];

  const checkIfLoggedIn = () => {
    let value = getStorage();
    let page = window.location.href.split(window.location.host)[1];

    let allPages = ['reset-password', 'set-password', 'forgot-password', 'manage-user', 'consumers', 'dashboard', 'suppliers', 'products', 'taxes', 'units'
      , 'purchase', 'invoice', 'delivery-challan', 'invoicereturn', 'stock', 'stock-adjustment', 'stock-adjustment-save', 'stock-transfer', 'stock-transfer-save', 'user', 'staff',
      'product-category', 'branches', 'notifications', 'email-settings-save', 'whatsapp-settings-save', 'firms', 'buyers', 'gst-report', 'my-profile', 'product-category-save', 'product-save', 'unit-save', 'purchase-save', 'PurchaseSave',
      'invoice-Setting', 'invoice-save', 'SaleSaveReturn', 'SaleSave', 'buyer-save', 'supplier-save', 'user-save'
      , 'consumer-save', 'staff-save', 'tax-save', 'branch-save', 'firm-save', 'help-center', 'delivery-challan-save', 'customer-ledger', 'sale-register', 'sale-Dispatch-summary', 'balance-sheet',
      'ledger-group-summary', 'ledger-summary', 'ledger-voucher', 'day-book', 'daily-purchases-report', 'stock-transfer-summary', 'daily-sales-report', 'staff-summary-report',
      'sales-details-by-saleperson',
      , 'sales-summery-by-customer', 'outstanding-receivable-summary', 'outstanding-payable-summary', 'shipping-address', 'shipping-address-save', 'credit-note', 'credit-note-save',
      'debit-note', 'debit-note-save', 'settings', 'report-menu', 'ledger', 'ledger-save', 'purchase-return', 'sale-return',
      'transporters', 'transporter-save', "payment-made", "payment-made-save", "payment-received", "payment-received-save", "voucher", "voucher-save", "journal-voucher",
      "journal-voucher-save", "bank-reconciliation"
      , "warehouses", "warehouse-save", "ledger-groups", "ledger-group-save", "stock-statement", "profit-and-loss", "terms", "privacy-policy", "trial-balance", "supplier-ledger", "purchase-register",
      "android", "subscription", "e-way-bill", "e-way-bill-update", "e-way-bill-extend"
      , "price-list", 'gstr1-report', 'pos', 'areawise-report', 'area', 'area-save', 'stock-summary-by-supplier-report', 'stock-summary-by-buyer-report', 'stock-summary-detail-by-product-buyer-report', 'stock-detail-by-buyer-report',
      'stock-summary-by-product-buyer-report', 'free-good-summary-by-category-report',
      'free-good-detail-by-buyer-report', 'free-good-summary-by-buyer-report', 'stock-by-category', 'stock-by-product', 'stock-reports', 'quotation-detail-report',
      'purchase-detail-report', 'sale-order-due-report',
      'gstr2-report', 'gstr3B-report', 'user-group', 'user-group-save', 'outstanding-payable', 'outstanding-receivable', 'outstanding-receivable-new', 'account-and-stock-transfer',
      'config-barcode', 'from-to-address', 'from-to-address-save', 'sale-by-customer', 'sales-summery-by-Product', 'sale-by-product','sales-summary-by-customer-and-product', 'purchase-summary-by-supplier', 'purchase-details-by-supplier',
      'purchase-summary-by-Product', 'purchase-details-by-product', 'stock-by-warehouse', 'cash-receive-party-wise', 'outstanding-payable-new', 'user-group', 'user-group-save',
      'user-group-access', 'purchase-analysis-summary-report', 'ledger-group-summary-report', 'sales-analysis-summary', 'staging-stock-transfer', 'staging-stock-transfer-save',
      'vehicle', 'vehicle-save', 'vehicle-type',
      'vehicle-type-save', 'staff-type', 'staff-type-save', 'documents', 'document-save', 'expense', 'expense-category-save', 'expense-item', 'expense-item-save', 'income-item', 'income-item-save', 'label-summary',
      'label-detail', 'project-level-party-balance-report', 'expense-summary-by-vehicle', 'expense-summary-by-category', 'expense-summary-by-account',
      'label', 'label-save', 'bom', 'bom-save', 'bom-invoice-save', 'expense-voucher', 'expense-voucher-save', 'lr', 'lr-save', 'lr-expense-detail', 'lr-expense-item-usage',
      'lr-monthly-summary-by-vehicle', 'lr-summary-by-vehicle', 'ledger-group-summary-detail-report', 'purchase-order', 'purchase-order-save'
      , 'sale-order-due-detail-by-product', 'purchase-order-due-detail-by-product', 'purchase-order-due-summary-by-product', 'challan-due-summary-by-product', 'challan-due-detail-by-product', 'possible-bom-count-by-available-stock', 'credit-note-register-report', 'debit-note-register-report','customer-party-inactivity-report',
      , 'product-company', 'product-company-save', 'item', 'item-save','party-type', 'party-type-save', 'income-voucher', 'income-voucher-save', 'manufacturing-journal', 'manufacturing-journal-save', 'doctor', 'doctor-save','stock-ledger-report','stock-summary-ledger','configuration','preferences'
    ];

    if (value && value.token && value.token.length > 0) {
      if (value.userId > 0) {
        if (value.consumerId > 0) {
          if (page && page !== null && page !== undefined) {
            let curPage = page.split('/')[1];

            //for Query params
            let isParams = curPage.includes("?");
            if (isParams === true) {
              curPage = curPage.split('?')[0];
            }

            if (curPage !== "") {
              let isPage = allPages.findIndex((x) => x === curPage);
              if (isPage >= 0) {
                // navigate(page);
              } else {
                navigate("/not-found");
              }
            } else {
              navigate("/dashboard");
            }
          } else {
            // history.push("/dashboard");
            navigate("/not-found");
          }
        }
        else {
          if (page && page != null && page !== undefined) {
            let curPage = page.split('/')[1];

            //for Query params
            let isParams = curPage.includes("?");
            if (isParams === true) {
              curPage = curPage.split('?')[0];
            }

            if (curPage !== "") {
              let isPage = allPages.findIndex((x) => x === curPage);
              if (isPage >= 0) {
              } else {
                navigate("/not-found");
              }
            } else {
              navigate("/consumers");
            }
          } else {
            navigate("/");
          }

        }
      }
      else {

        if (page && page != null && page !== undefined) {
          let curPage = page.split('/')[1];

          //for Query params
          let isParams = curPage.includes("?");
          if (isParams === true) {
            curPage = curPage.split('?')[0];
          }

          if (curPage !== "") {
            let isPage = publicPages.findIndex((x) => x === curPage);
            if (isPage >= 0) {
              // navigate(page);
            } else {
              navigate("/");
            }
          }
          else {
            navigate("/");
          }

        } else {
          navigate("/");
        }
      }
    }
    else {
      if (page && page != null && page !== undefined) {
        let curPage = page.split('/')[1];

        //for Query params
        let isParams = curPage.includes("?");
        if (isParams === true) {
          curPage = curPage.split('?')[0];
        }

        if (curPage !== "") {
          let isPage = publicPages.findIndex((x) => x === curPage);
          if (isPage >= 0) {
            // navigate(page);
          } else {
            //history.push("/not-found");
            navigate("/");
          }
        }
        else {
          navigate("/");
        }
      } else {
        navigate("/");
      }
    }
    setLoading(false);
  }
  async function init() {
    try {
      const data: any = await Info(null);
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    checkIfLoggedIn();
  }, [location.pathname]);


  useEffect(() => {
    init();
    // setTimeout(() => setLoading(false), 3000);
  }, []);
  useEffect(() => {
  const handleKeyDown = (event: KeyboardEvent) => {
    // F-Keys
    if (event.key === "F2") {
      event.preventDefault();
      window.dispatchEvent(new CustomEvent("appShortcut", { detail: { type: "add" } }));
    }
    if (event.key === "F10") {
      event.preventDefault();
      window.dispatchEvent(new CustomEvent("appShortcut", { detail: { type: "save" } }));
    }

    // Letter Shortcuts (Alt + I/P or Cmd + I/P)
    const isModifierPressed = event.altKey || event.metaKey;

    if (isModifierPressed && event.code === "KeyI") {
      event.preventDefault();
      window.dispatchEvent(new CustomEvent("appShortcut", { detail: { type: "add" } }));
    }

    if (isModifierPressed && event.code === "KeyP") {
      event.preventDefault();
      window.dispatchEvent(new CustomEvent("appShortcut", { detail: { type: "save" } }));
    }

    // ✅ ESCAPE KEY - Smart Navigation
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      
      // If already on dashboard, do nothing
      if (location.pathname === "/dashboard" || location.pathname === "/") {
        return;
      }
      // For other pages, go back one page
      navigate(-1);
    }
  };
  window.addEventListener("keydown", handleKeyDown);
  return () => window.removeEventListener("keydown", handleKeyDown);
}, [navigate, location.pathname]);

  return (
    <>
      <ColorModeProvider>
        {isOnline ?
          <>
            <CssBaseline />
            {showMaintenanceBanner && <MaintenanceBanner />}
            {showAppSplash ?
              <SplashComponent></SplashComponent>
              : <PreferencesContext.Provider value={preferences}>
                {userContext && !userContext?.isUnauthorized &&
                  <>
                    <Alerts isOpen={userContext.isAlert} displayMessage={userContext.alertMessage} type={userContext.alertType}></Alerts>
                  </>
                }
                <UnauthorizeDialog isOpen={userContext?.isUnauthorized} />
                <Suspense fallback={
                  isDashboardRoute ? null : <SplashComponent></SplashComponent>
                }>
                  <Box
                    sx={{
                      height: isLoggedIn ? "auto" : "100%",
                      minHeight: isLoggedIn ? undefined : "100%",
                      boxSizing: "border-box",
                      paddingTop:
                        showMaintenanceBanner && !isLoggedIn
                          ? `${MAINTENANCE_BANNER_HEIGHT}px`
                          : 0,
                    }}
                  >
                    <Outlet />
                  </Box>
                  <ScrollTop />
                </Suspense>
              </PreferencesContext.Provider>
            }
          </>
          :
          <Box className="offline-img-box">
            <img className="offline-img" src={offlineimg} alt="you are offline" />
          </Box>
        }
      </ColorModeProvider>
    </>
  );
}
export default App;